import { memo, useCallback } from 'react'
import {
  Box,
  Slider,
  TextField,
  InputAdornment,
  Typography,
  Button,
} from '@mui/material'
import { SxProps, Theme } from '@mui/system'
import ArrowDropUp from '@mui/icons-material/ArrowDropUp'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import { QuestionnaireWithSections } from '../types'
import { Pagination } from './Pagination'
import { IconName } from '../../../../../../../utils/icons'

interface CriticalSectionsPanelProps {
  data: QuestionnaireWithSections[]
  searchValue: string
  onSearchChange: (value: string) => void
  rangeValue: [number, number]
  onRangeChange: (value: [number, number]) => void
  onApplyRange: () => void
  isRangeDirty: boolean
  expandedIds: Set<string>
  onToggle: (id: string) => void
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  loading?: boolean
  styles: {
    processPanel: SxProps<Theme>
    processPanelHeader: SxProps<Theme>
    processPanelTitle: SxProps<Theme>
    processPanelHeaderRight: SxProps<Theme>
    sliderContainer: SxProps<Theme>
    applyButton: SxProps<Theme>
    searchField: SxProps<Theme>
    questionnaireHeader: SxProps<Theme>
    questionnaireHeaderText: SxProps<Theme>
    questionnaireHeaderArrow: SxProps<Theme>
    sectionBarBlue: SxProps<Theme>
    sectionBarBlueText: SxProps<Theme>
    questionBarGrayContainer: SxProps<Theme>
    questionBarGrayLabel: SxProps<Theme>
    questionBarGrayTrack: SxProps<Theme>
    questionBarGrayFill: SxProps<Theme>
    questionBarGrayValue: SxProps<Theme>
    paginationContainer: SxProps<Theme>
    paginationButton: SxProps<Theme>
    paginationPage: SxProps<Theme>
    paginationPageActive: SxProps<Theme>
    paginationDisabled: SxProps<Theme>
  }
  getScoreColor: (score: number) => string
  icons: (name: IconName) => React.ReactNode
  t: (key: string, options?: Record<string, unknown>) => string
}

const CriticalSectionsPanel = ({
  data,
  searchValue,
  onSearchChange,
  rangeValue,
  onRangeChange,
  onApplyRange,
  isRangeDirty,
  expandedIds,
  onToggle,
  page,
  totalPages,
  onPageChange,
  loading = false,
  styles,
  getScoreColor,
  icons,
  t,
}: CriticalSectionsPanelProps) => {
  const handleSliderChange = useCallback(
    (_: Event, value: number | number[], _activeThumb: number) => {
      onRangeChange(value as [number, number])
    },
    [onRangeChange]
  )

  return (
    <Box sx={styles.processPanel}>
      {/* Header */}
      <Box sx={styles.processPanelHeader}>
        <Typography sx={styles.processPanelTitle}>
          {t('dashboard.storePerformance.processStatus.criticalSections')}
        </Typography>
        <Typography sx={styles.processPanelHeaderRight}>
          {t('dashboard.storePerformance.processStatus.resultRange', {
            min: rangeValue[0],
            max: rangeValue[1],
          })}
        </Typography>
      </Box>

      {/* Slider + Apply */}
      <Box sx={styles.sliderContainer}>
        <Box sx={{ width: '98%', margin: '0 auto' }}>
          <Slider
            value={rangeValue}
            onChange={handleSliderChange}
            min={0}
            max={100}
            step={10}
            marks
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}%`}
            sx={{
              color: '#4a90e2',
              '& .MuiSlider-rail': {
                height: 8,
                backgroundColor: '#a8d0f7',
                opacity: 1,
                borderRadius: '4px',
              },
              '& .MuiSlider-track': {
                height: 8,
                backgroundColor: '#4a90e2',
                border: 'none',
                borderRadius: '4px',
              },
              '& .MuiSlider-thumb': {
                width: 12,
                height: 12,
                backgroundColor: '#4a90e2',
                boxShadow: 'none',
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0 0 0 6px rgba(74, 144, 226, 0.2)',
                },
              },
              '& .MuiSlider-mark': {
                backgroundColor: '#4a90e2',
                width: 5,
                height: 5,
                borderRadius: '50%',
              },
              '& .MuiSlider-markActive': {
                backgroundColor: '#fff',
              },
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            sx={styles.applyButton}
            onClick={onApplyRange}
            disabled={!isRangeDirty}
          >
            {t('dashboard.storePerformance.processStatus.apply')}
          </Button>
        </Box>
      </Box>

      {/* Search */}
      <Box sx={{ padding: '12px 14px 16px 16px' }}>
        <TextField
          placeholder={t('dashboard.storePerformance.search')}
          size="small"
          fullWidth
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={styles.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {icons('search')}
              </InputAdornment>
            ),
          }}
        />
      </Box>


      {/* Questionnaires list */}
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '13px', color: '#9ca3af' }}>
              {t('general.loading')}
            </Typography>
          </Box>
        ) : (
          <>
            {data.map((q) => {
          const isExpanded = expandedIds.has(q.id)
          const hasSections = q.sections.length > 0
          return (
            <Box key={q.id}>
              {/* Questionnaire header (collapsible or flat) */}
              <Box
                sx={isExpanded && hasSections
                  ? { ...styles.questionnaireHeader, marginBottom: '0', borderRadius: '10px 10px 0 0' }
                  : styles.questionnaireHeader
                }
                onClick={hasSections ? () => onToggle(q.id) : undefined}
                role={hasSections ? 'button' : undefined}
                tabIndex={hasSections ? 0 : undefined}
                onKeyDown={hasSections ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onToggle(q.id)
                  }
                } : undefined}
                style={!hasSections ? { cursor: 'default' } : undefined}
              >
                <Typography sx={styles.questionnaireHeaderText}>
                  {q.name}
                </Typography>
                {hasSections && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Box sx={styles.questionnaireHeaderArrow}>
                      {isExpanded ? <ArrowDropUp sx={{ fontSize: '30px' }} /> : <ArrowDropDown sx={{ fontSize: '30px' }} />}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Expanded content: sections */}
              {isExpanded && hasSections && (
                <Box sx={{
                  mb: '14px',
                  mx: '16px',
                  border: '1px solid #82828280',
                  borderTop: 'none',
                  borderRadius: '0 0 10px 10px',
                  overflow: 'hidden',
                }}>
                  {q.sections.map((section) => (
                    <Box key={section.id}>
                      {/* Section bar (blue) */}
                      <Box sx={styles.sectionBarBlue}>
                        <Box sx={styles.sectionBarBlueText}>
                          <span>{section.name}</span>
                          <span>{section.score}%</span>
                        </Box>
                      </Box>

                      {/* Questions */}
                      {section.questions.map((question) => {
                        const barColor = getScoreColor(question.score)
                        return (
                          <Box key={question.id} sx={styles.questionBarGrayContainer}>
                            <Typography sx={styles.questionBarGrayLabel}>
                              {question.name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Box sx={{ ...styles.questionBarGrayTrack, width: '100%' }}>
                                <Box
                                  sx={{
                                    ...styles.questionBarGrayFill,
                                    width: `${question.score}%`,
                                    backgroundColor: barColor,
                                  }}
                                />
                              </Box>
                              <Typography sx={styles.questionBarGrayValue}>
                                {question.score}%
                              </Typography>
                            </Box>
                          </Box>
                        )
                      })}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )
        })}

            {data.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '13px', color: '#9ca3af' }}>
                  {t('general.ninformationNotFound')}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 'auto', p: '12px 16px' }}>
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
          maxVisiblePages={2}
          styles={{
            paginationContainer: styles.paginationContainer,
            paginationButton: styles.paginationButton,
            paginationPage: styles.paginationPage,
            paginationPageActive: styles.paginationPageActive,
            paginationDisabled: styles.paginationDisabled,
          }}
        />
      </Box>
    </Box>
  )
}

const CriticalSectionsPanelMemo = memo(CriticalSectionsPanel)
export { CriticalSectionsPanelMemo as CriticalSectionsPanel }
